<template>
  <sticky-header-layout>
    <!-- Header -->
    <template #header>
      <div :class="['action-container', { 'justify-content-end': isOneLineEditProductMode }]">
        <el-button
          v-if="!isOneLineEditProductMode"
          :disabled="isLoading || isSaving"
          class="add-editorial-item-button"
          size="mini"
          type="primary"
          icon="el-icon-plus"
          @click="handleAddProductItem"
        >
          {{ $t('button.addNewItem') }}
        </el-button>
        
        <span>
          <el-button
            :disabled="isSaving"
            class="cancel-button"
            size="mini"
            type="warning"
            icon="el-icon-close"
            @click="handleCancel"
          >
            {{ $t('button.cancel') }}
          </el-button>
          
          <el-button
            :disabled="isLoading"
            :loading="isSaving"
            class="save-button"
            size="mini"
            type="danger"
            icon="el-icon-upload2"
            @click="handleSave"
          >
            {{ $t('button.save') }}
          </el-button>
        </span>
      </div>
    </template>
    <!-- Header -->

    <div v-loading="isLoading">
      <generic-form
        ref="form"
        :attributes="{ disabled: isSaving, labelWidth: '152px' }"
        :form="form"
        :form-template="formTemplate"
        class="pb-3"
      >
        <template v-for="(row, index) in formTemplate">
          <!-- Products -->
          <div
            v-if="row.key === 'products'"
            :key="row.key"
            :style="{ order: index }"
          >
            <h4 class="title">{{ $t('table.products') }}</h4>

            <el-checkbox
              v-if="isEditMode"
              v-model="isOneLineEditProducts"
              class="pl-2"
            >
              {{ $t('table.oneLineEdit') }}
            </el-checkbox>

            <div 
              v-if="isOneLineEditProductMode"
              class="form-group filter-option mr-2"
            >
              <span class="pr-2">Keywords</span>

              <el-input
                v-model="productSearch.keywords"
                clearable
              />
            </div>

            <div 
              v-if="isOneLineEditProductMode"
              class="form-group filter-option mr-2"
            >
              <span class="pr-2">Gender: {{ form.product_preferred_gender }}</span>
            </div>

            <div
              v-if="isOneLineEditProductMode"
              v-for="filter in productSearchFilters"
              :key="`product-searchs-${filter.type}`"
              class="form-group filter-option mr-2"
            >
              <span class="pr-2">{{ $t(`common.${filter.type}`) }}</span>
              
              <el-select
                v-model="productSearch[`${filter.type}`]"
                :placeholder="$t('placeholder.select')"
                :remote-method="filter.type === 'brands' ? updateProductSearchBrandListByKeyword : filter.type === 'stores' ? updateProductSearchStoreListByKeyword : null"
                class="w-100"
                remote
                filterable
                multiple
              >
                <el-option
                  v-for="item in filter.options"
                  :key="item.slug"
                  :label="item.name"
                  :value="item.slug"
                />
              </el-select>
            </div>

            <el-button
              v-if="isOneLineEditProductMode"
              v-loading="isFetchingProductIds"
              :disabled="isLoading || isSaving"
              class="add-editorial-item-button"
              style="margin-left:120px;margin-bottom:10px;"
              size="mini"
              type="primary"
              @click="handleAutoFillProductIDs"
            >
              Auto Fill Product IDs
            </el-button>

            <div>
              <div 
                v-for="product in productSearchResult"
                class="product-cell"
              >
                <img :src="product.main_image_mobile" />
              </div>
            </div>

            <el-form-item
              v-if="isOneLineEditProductMode"
              :label="$t('table.id')"
              :rules="formRules.ref_id"
              prop="products"
              style="clear:both;"
            >
              <el-input
                v-model.trim="form.products"
                clearable
              />

              <div class="one-line-image">
                <template v-for="productId in form.products.split(',')">
                  <img
                    v-if="productImages[productId]"
                    :key="`product-image-${productId}`"
                    :src="productImages[productId]"
                    class="product-image"
                  >
                </template>
              </div>
            </el-form-item>

            <el-table
              v-else
              :data="form.products"
              row-key="key"
            >
              <!-- Product ID Field -->
              <el-table-column
                :label="$t('table.id')"
                align="center"
                min-width="200"
              >
                <template #default="{ row, $index }">
                  <el-form-item
                    :label="`Product ${$index + 1} ID`"
                    :prop="`products.${$index}.id`"
                    :rules="formRules.product_id"
                  >
                    <el-input
                      v-model.trim="row.id"
                      clearable
                    />
                  </el-form-item>
                </template>
              </el-table-column>
              <!-- Product ID Field -->

              <!-- Product Image Preview -->
              <el-table-column
                v-if="hasProductImage"
                :label="$t('table.image')"
                align="center"
                width="120"
              >
                <template #default="{ row }">
                  <img
                    :src="row.img"
                    class="product-image"
                  >
                </template>
              </el-table-column>
              <!-- Product Image Preview -->

              <!-- Delete Button -->
              <el-table-column
                :label="$t('table.action')"
                align="center"
                width="60"
              >
                <template #default="{ $index }">
                  <el-button
                    size="mini"
                    type="danger"
                    icon="el-icon-delete"
                    circle
                    @click="handleDeleteProductItem($index)"
                  />
                </template>
              </el-table-column>
              <!-- Delete Button -->

              <!-- Drag Button -->
              <el-table-column
                align="center"
                label="Drag"
                width="55"
              >
                <span class="drag-handler mt-1">
                  <font-awesome-icon
                    class="drag"
                    icon="arrows-alt"
                  />
                </span>
              </el-table-column>
              <!-- Drag Button -->
            </el-table>
          </div>
          <!-- Products -->

          <!-- Promotion -->
          <div
            v-if="row.key === 'appPromotionDetails'"
            :key="row.key"
            :style="{ order: index }"
            class="promotion-wrapper"
          >
            <generic-form-element
              v-for="(row, index) in promotionFormTemplate"
              :key="`promotion.${row.key}`"
              :row="row"
              :index="index + 1"
              :form="form.promotion"
              :class="{ 'pt-4': row.key === 'main_img' }"
              :disabled="row.key !== 'status' && !form.promotion.status"
            />
          </div>          
          <!-- Promotion -->

          <!-- Redirect Filters -->
          <div
            v-loading="isFetchingRedirectFilters"
            v-if="row.key === 'redirectFiltersSelects'"
            :key="row.key"
            :style="{ order: index }"
          >
            <div
              v-if="!isEditMode"
              class="pl-3 d-flex auto-fill-option"
            >
              <the-tooltip
                class="auto-fill-option"
                label="This option will use the first product to generate the below redirect filters automatically."
              >
                <el-checkbox v-model="isRedirectFilterAutoFilled">
                  Auto-Fill redirect info
                </el-checkbox>
              </the-tooltip>
              <transition name="fade">
                <i
                  v-if="isFetchingProduct"
                  class="el-icon-loading ml-1"
                />
              </transition>
            </div>

            <div
              v-for="filter in redirectFilters"
              :key="`redirect-${filter.type}`"
              class="form-group filter-option mr-2"
            >
              <span class="pr-2">{{ $t(`common.${filter.type}`) }}</span>
              
              <el-select
                v-model="form[`product_not_available_redirect_${filter.type}`]"
                :placeholder="$t('placeholder.select')"
                :remote-method="filter.type === 'brands' ? updateBrandListByKeyword : filter.type === 'stores' ? updateStoreListByKeyword : null"
                :disabled="isRedirectFilterAutoFilled && !isEditMode"
                class="w-100"
                remote
                filterable
                multiple
                @visible-change="visibility => onFilterVisibilityChange(visibility, filter.type)"
              >
                <!-- Avoid rendering options when not visible, in order to make text input much smoother -->
                <template v-if="filter.isVisible">
                  <el-option
                    v-for="item in filter.options"
                    :key="item.slug"
                    :label="filter.type === 'categories' ? item.slug : item.name"
                    :value="item.slug"
                  />
                </template>
              </el-select>
            </div>
          </div>
          <!-- Redirect Filters -->
        </template>
      </generic-form>
    </div>
  </sticky-header-layout>
</template>

<script>
import Sortable from 'sortablejs'
import debounce from 'lodash/debounce'

import GenericForm from '../../components/form/Form.vue'
import GenericFormElement from '../../components/form/FormElement.vue'
import TheTooltip from '../../components/TheTooltip.vue'

import { categoryList } from '../../utils/const'
import { countryList, ServerToFormData, FormToServerFormat, FormRules } from '../../utils/form'
import { getRandomHash } from '../../utils/helper'
import { checkImageURL } from '../../utils/validate'
import VaderUtils from '../../utils/vader'

import BannerService from '../../api/banner'
import VaderService from '../../api/vader'

import {
  genders,
  userTypes,
  getDefaultPromotionDetail,
} from '../../const/banner'

const TITLE_MAX_CHAR = 50
export default {
  name: 'BannerDetail',
  components: {
    GenericForm,
    GenericFormElement,
    TheTooltip,
  },
  props: {
    id: {
      type: [String, Number],
      default: undefined,
    },
  },
  data() {
    const formRules = {
      available_countries: [FormRules.requiredCountries],
      url: [FormRules.url],
      imageUrl: [FormRules.imageUrl],
      product_id: [FormRules.id, FormRules.required],
      ref_id: [FormRules.idList],
    }
    const formTemplate = [
      {
        key: 'title',
        isSimpleLocalized: true,
        title: `${this.$t('table.title')} (${this.$t('table.maxCharacters', { number: TITLE_MAX_CHAR })})`,
        custom: { maxlength: TITLE_MAX_CHAR },
      },
      {
        key: 'desc',
        isSimpleLocalized: true,
        title: `${this.$t('table.subtitle')} (${this.$t('table.maxCharacters', { number: TITLE_MAX_CHAR })})`,
        custom: { maxlength: TITLE_MAX_CHAR },
      },
      {
        key: 'img',
        isSimpleLocalized: true,
        title: this.$t('table.bannerImageUrl'),
        rules: formRules.imageUrl,
        custom: { placeholder: 'https://' },
      },
      { key: 'basicInfo', isTitle: true },
      {
        key: 'url',
        title: 'URL',
        isInput: true,
        custom: { placeholder: 'https://' },
        rules: formRules.url,
      },
      {
        key: 'available_countries',
        isCheckbox: true,
        title: this.$t('table.availableCountries'),
        list: countryList,
        rules: formRules.available_countries,
      },
      { key: 'status', isSwitch: true, title: this.$t('table.enable') },
      {
        key: 'product_preferred_gender',
        isRadio: true,
        list: genders,
        title: this.$t('table.preferredGender'),
      },
      { key: 'appPromotionInfo', isTitle: true },
      { key: 'appPromotionDetails', isSlot: true },
      { key: 'products', isSlot: true },
      { key: 'redirectFilters', isTitle: true },
      { key: 'redirectFiltersSelects', isSlot: true },
    ]
    const promotionFormTemplate = [
      { key: 'status', isSwitch: true, title: this.$t('table.enable') },
      { key: 'txt_1', isSimpleLocalized: true, custom: { placeholder: this.$t('placeholder.bannerTitle') } },
      { key: 'txt_2', isSimpleLocalized: true, custom: { placeholder: this.$t('placeholder.bannerDescription') } },
      {
        key: 'main_img',
        isInput: true,
        title: this.$t('table.image'),
        rules: formRules.imageUrl,
        prop: 'promotion.main_img',
        custom: { placeholder: 'https://' },
      },
    ]

    const isEditMode = !!this.id
    
    return {
      isEditMode,
      isEdited: false,
      isLoading: false,
      isSaving: false,
      isFetchingRedirectFilters: false,
      isFetchingProductSearchFilters: false,
      isFetchingProductIds: false,
      isOneLineEditProducts: false,
      availableCountries: countryList,
      categoryList,
      formTemplate,
      form: {
        available_countries: [...countryList],
        product_preferred_gender: '',
        products: isEditMode ? [] : '',
        status: false,
        url: '',
        title: '',
        title_zh_hk: '',
        title_zh_cn: '',
        desc: '',
        desc_zh_hk: '',
        desc_zh_cn: '',
        img: '',
        img_zh_hk: '',
        img_zh_cn: '',
        promotion: getDefaultPromotionDetail(),
      },
      formRules,
      productSearch: {
        keywords: '',
        brands: [],
        stores: [],
      },
      productSearchFilters: [
        { type: 'brands', options: [] },
        { type: 'stores', options: [] },
      ],
      redirectFilters: [],
      productSearchResult: [],
      promotionFormTemplate,
      productImages: {},
      isRedirectFilterAutoFilled: true,
      isFetchingProduct: false,
    }
  },
  computed: {
    hasProductImage() {
      return this.form.products.some(product => !!product.img)
    },
    isPromotionEnabled() {
      return this.form.promotion.status
    },
    isOneLineEditProductMode() {
      return !this.isEditMode || this.isOneLineEditProducts
    },
    autoFilterProductId() {
      if (!this.isEditMode) {
        return this.form.products.split(',')[0]
      }
      return null
    },
  },
  watch: {
    form: {
      handler() {
        this.isEdited = true
      },
      deep: true,
    },
    isOneLineEditProducts(value) {
      if (value) {
        this.form.products = this.form.products.map(({ id }) => id).join(',')
      } else {
        this.form.products = this.form.products.split(',').map(id => ({
          id,
          img: this.productImages[id],
          key: getRandomHash(),
        }))
        this.$nextTick(() => {
          this.setSort()
        })
      }
    },
    isRedirectFilterAutoFilled(value) {
      if (value) {
        this.getVaderProduct(this.autoFilterProductId)
      }
    },
    autoFilterProductId: debounce(function _(value) {
      this.getVaderProduct(value)
    }, 300),
  },
  mounted() {
    if (this.isEditMode) {
      this.getBannerDetail(this.id)
    } else {
      this.setSort()
    }
    this.getRedirectFilters()
  },
  beforeRouteLeave(to, from, next) {
    if (this.isSaving) {
      return next(false)
    }
    if (!this.isEdited) {
      return next()
    }

    this.$confirm(this.$t('message.leaveWithUnsavedChanges'), this.$t('common.warning'), {
      confirmButtonText: this.$t('button.leave'),
      cancelButtonText: this.$t('button.cancel'),
      type: 'warning',
    }).then(() => next()).catch(() => next(false))

    return next(false)
  },
  methods: {
    setSort() {
      const el = document.querySelectorAll('.el-table__body-wrapper > table > tbody')[0]
      if (!el) return
      this.sortableObject = Sortable.create(el, {
        ghostClass: 'sortable-ghost',
        handle: '.drag-handler',
        setData(dataTransfer) {
          dataTransfer.setData('Text', '')
          // to avoid Firefox bug
          // Detail see : https://github.com/RubaXa/Sortable/issues/1012
        },
        onMove: (evt) => {
          // disable drag & drop while saving
          if (this.isSaving) {
            evt.preventDefault()
            return false
          }
          return true
        },
        onEnd: (evt) => {
          const targetRow = this.form.products.splice(evt.oldIndex, 1)[0]
          this.form.products.splice(evt.newIndex, 0, targetRow)
        },
      })
    },
    goToListPage() {
      this.$router.push({ path: '/banner/list' })
    },
    reshapeServerToFormData(detail) {
      // Basic info
      detail.status = !!detail.status
      detail.available_countries = ServerToFormData.getAvailableCountriesList(detail.available_countries)

      if (Array.isArray(detail.promotion)) {
        // set promotion details as default when not set before
        detail.promotion = getDefaultPromotionDetail(true, detail)
      } else {
        detail.promotion.status = !!detail.promotion.status
      }

      // products
      detail.products = detail.products.map(product => ({
        ...product,
        key: getRandomHash(), // unique key to avoid id clash
      }))
      detail.products.forEach(({ id, img }) => {
        this.productImages[id] = img
      })
      this.form = detail
    },
    reshapeFormDataToServerFormat(form) {
      const newForm = { ...form }

      newForm.available_countries = FormToServerFormat.getCountryAvailabilityMap(newForm.available_countries)
      if (Array.isArray(newForm.products)) {
        newForm.products.forEach((product) => { delete product.key }) // delete key used for el-table only
      } else {
        newForm.products = newForm.products
          ? newForm.products.split(',').map(id => ({ id })) // convert to list of id for server
          : [] // return empty list if products is empty string
      }

      newForm.promotion.status = Number(newForm.promotion.status)

      if (!newForm.ordering) newForm.ordering = 0
      return newForm
    },
    handleAddProductItem() {
      this.form.products.push({
        key: getRandomHash(),
        id: '',
      })
      this.$nextTick(() => {
        this.setSort()
      })
    },
    handleAutoFillProductIDs() {
      this.getProductIds()
    },
    handleDeleteProductItem(index) {
      this.form.products.splice(index, 1)
    },
    handleCancel() {
      this.goToListPage()
    },
    handleSave() {
      this.$refs.form.$refs.form.validate().then(() => {
        const serverForm = this.reshapeFormDataToServerFormat(this.form)
        if (this.isEditMode) {
          this.editBanner(serverForm, this.id)
        } else {
          this.addBanner(serverForm)
        }
      }).catch(() => {
        this.$alert(this.$t('error.pleaseFillInAndCheckErrors'), this.$t('common.oops'), {
          confirmButtonText: this.$t('button.ok'),
        })
      })
    },
    handleSaveSuccess() {
      this.isSaving = false
      this.isEdited = false
      this.goToListPage()
    },
    handleSaveFail(error) {
      this.isSaving = false
      this.$alert(`Error: ${error}`, this.$t('error.somethingWentWrong'), {
        type: 'error',
        confirmButtonText: this.$t('button.ok'),
      })
    },
    resetRedirectFilters() {
      this.form.product_not_available_redirect_brands = []
      this.form.product_not_available_redirect_categories = []
      this.form.product_not_available_redirect_stores = []
    },
    async getBannerDetail(id) {
      this.isLoading = true
      try {
        const { data } = await BannerService.getDetail(id)
        this.reshapeServerToFormData(data)
        this.$nextTick(() => {
          this.isEdited = false
          this.setSort()
        })
        this.isLoading = false
      } catch (error) {
        console.error(error)
        this.isLoading = false
        this.$alert(this.$t('error.bannerNotFound'), this.$t('common.error'), {
          type: 'error',
          confirmButtonText: this.$t('button.ok'),
        }).then(() => {
          this.goToListPage()
        })
      }
    },
    async editBanner(form) {
      this.isSaving = true
      try {
        await BannerService.updateDetail(form, this.id)
        this.handleSaveSuccess()
      } catch (error) {
        this.handleSaveFail(error)
      }
    },
    async addBanner(form) {
      this.isSaving = true
      try {
        await BannerService.create(form)
        this.handleSaveSuccess()
      } catch (error) {
        this.handleSaveFail(error)
      }
    },
    async getRedirectFilters() {
      this.isFetchingRedirectFilters = true
      try {
        const { data: { data: categoriesData } } = await VaderService.getCategories()
        const categories = VaderUtils.flattenCategoryTree(categoriesData)

        this.redirectFilters = [
          { type: 'categories', options: categories, isVisible: false },
          { type: 'brands', options: [], isVisible: true },
          { type: 'stores', options: [], isVisible: true },
        ]
      } catch (error) {
        console.error(error)
      } finally {
        this.isFetchingRedirectFilters = false
      }
    },
    async getProductIds() {
      this.isFetchingProductIds = true

      let params = {
        country: 'hk',
        size: 24
      }

      if (this.productSearch.keywords !== '') {
        params.keywords = this.productSearch.keywords
      }

      if (this.form.product_preferred_gender !== '') {
        params.genders = this.form.product_preferred_gender
      }

      if (this.productSearch.brands.length > 0) {
        params.brands = this.productSearch.brands.join(',')
      }

      if (this.productSearch.stores.length > 0) {
        params.stores = this.productSearch.stores.join(',')
      }

      try {
        const { data: { data: products } } = await VaderService.getProductByFilter(params)

        this.productSearchResult = products

        let productIds = []

        products.forEach(product => {
          productIds.push(product.id)
        })
        
        this.form.products = productIds.join(',')
      } catch (error) {
        console.error(error)
      } finally {
        this.isFetchingProductIds = false
      }
    },
    async getVaderProduct(id) {
      if (!id) {
        this.resetRedirectFilters()
        return
      }
      if (!Number(id)) return

      this.isFetchingProduct = true
      try {
        const { data } = await VaderService.getProductById(id, {
          skip_country: 1,
        })
        const {
          brand_slug: brand,
          category_slug: category,
          store_slug: store,
        } = data.data
        this.form.product_not_available_redirect_brands = [brand]
        this.form.product_not_available_redirect_categories = [category]
        this.form.product_not_available_redirect_stores = [store]
      } catch (error) {
        console.error(error)
      } finally {
        this.isFetchingProduct = false
      }
    },
    onFilterVisibilityChange(isVisible, filterType) {
      const index = this.redirectFilters.findIndex(filter => filter.type === filterType)
      this.redirectFilters[index].isVisible = isVisible
    },
    handleProductSearchFilterOptionChange(filterType, options) {
      const index = this.productSearchFilters.findIndex(filter => filter.type === filterType)
      this.productSearchFilters[index].options = options.filter(item => item.name)
    },
    handleRedirectFilterOptionChange(filterType, options) {
      const index = this.redirectFilters.findIndex(filter => filter.type === filterType)
      this.redirectFilters[index].options = options.filter(item => item.name)
    },
    async updateProductSearchBrandListByKeyword(query) {
      if (query !== '') {
        this.isFetchingProductSearchFilters = true

        try {
          const data = await VaderService.getBrands(query)
          const brands = data.data.data
          this.handleProductSearchFilterOptionChange('brands', brands)
        } finally {
          this.isFetchingProductSearchFilters = false
        }
      } else {
        this.options = []
      }
    },
    async updateProductSearchStoreListByKeyword(query) {
      if (query !== '') {
        this.isFetchingProductSearchFilters = true

        try {
          const data = await VaderService.getStores(query)
          const stores = VaderUtils.handleStoreNameCollision(data.data.data)
          this.handleProductSearchFilterOptionChange('stores', stores)
        } finally {
          this.isFetchingProductSearchFilters = false
        }
      } else {
        this.options = []
      }
    },
    async updateBrandListByKeyword(query) {
      if (query !== '') {
        this.isFetchingRedirectFilters = true
        
        try {
          const data = await VaderService.getBrands(query)
          this.isFetchingRedirectFilters = false
          const brands = data.data.data
          this.handleRedirectFilterOptionChange('brands', brands)
        } finally {
          this.isFetchingRedirectFilters = false
        }
      } else {
        this.options = []
      }
    },
    async updateStoreListByKeyword(query) {
      if (query !== '') {
        this.isFetchingRedirectFilters = true
        
        try {
          const data = await VaderService.getStores(query)
          this.isFetchingRedirectFilters = false
          const stores = VaderUtils.handleStoreNameCollision(data.data.data)
          this.handleRedirectFilterOptionChange('stores', stores)
        } finally {
          this.isFetchingRedirectFilters = false
        }
      } else {
        this.options = []
      }
    },
    checkImageURL,
  },
}
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/view-base.scss";
@import "~@/assets/styles/table.scss";
@import "~@/assets/styles/form.scss";

img {
  max-width: 100%;

  &.product-image {
    max-width: 100px;
    max-width: 100px;
  }
}

form .filter-option {
  flex-wrap: nowrap;
  align-items: center;

  span {
    width: 208px;
    font-size: 14px;
    text-align: right;
  }
}

.promotion-wrapper {
  ::v-deep {
    h4, .lang-box {
      margin-left: 92px;
    }
    .title {
      font-weight: 500;
      font-size: 15px;
      border-bottom: 1px solid #f1f1f1;
    }
  }
}

.main-img {
  max-height: 200px;
}

.one-line-image {
  max-height: 600px;
  overflow-y: auto;
}

.auto-fill-option {
  ::v-deep {
    .el-checkbox {
      margin-right: 4px;
    }
  }
}

.product-cell {
  float: left;
  width: 150px;
  height: 200px;
}

.product-cell img {
  width: 120px;
}
</style>
